import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Exercise,
  StudentExerciseAnswer,
  StudentModuleProgress,
  EvaluateExerciseResponse,
} from "@/services/student/exercise.interfaces";
import {
  loadModuleExercisesData,
  evaluateExerciseAndRefreshProgress,
} from "@/services/student/module-exercise-progress.service";

/**
 * Tipo de passo do carregamento serial
 */
export type LoadingStep = "idle" | "exercises" | "answers" | "progress";

/**
 * Interface do Context para gerenciamento de exercícios do módulo
 */
export interface ModuleExercisesContextType {
  // State
  moduleId: string | null;
  exercises: Exercise[];
  answers: Map<string, StudentExerciseAnswer>;
  progress: StudentModuleProgress | null;

  // Loading states
  isLoading: boolean;
  loadingStep: LoadingStep;
  isEvaluating: boolean;
  error: string | null;

  // Actions
  loadModule: (moduleId: string) => Promise<void>;
  evaluateExercise: (
    exerciseId: string,
    answer: any,
    exerciseType: string,
    overrideModuleId?: string
  ) => Promise<EvaluateExerciseResponse>;

  // Helpers
  getExercise: (exerciseId: string) => Exercise | undefined;
  getExerciseAnswer: (exerciseId: string) => StudentExerciseAnswer | undefined;
  isExerciseAnswered: (exerciseId: string) => boolean;
}

const ModuleExercisesContext =
  createContext<ModuleExercisesContextType | undefined>(undefined);

/**
 * Provider do Context de exercícios do módulo
 */
export const ModuleExercisesProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const [moduleId, setModuleId] = useState<string | null>(null);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [answers, setAnswers] = useState<
    Map<string, StudentExerciseAnswer>
  >(new Map());
  const [progress, setProgress] = useState<StudentModuleProgress | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState<LoadingStep>("idle");
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Carrega todos os dados do módulo - SERIAL
   * Ordem: 1. Exercícios → 2. Respostas → 3. Progresso
   */
  const loadModule = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);
    setModuleId(id);

    try {
      // Passo 1: Exercícios
      setLoadingStep("exercises");
      const exercisesData = await loadModuleExercisesData(id);
      setExercises(exercisesData.exercises);
      console.log(`✓ Loaded ${exercisesData.exercises.length} exercises`);

      // Passo 2: Respostas
      setLoadingStep("answers");
      const answersMap = new Map(
        exercisesData.answers.map((answer) => [answer.exerciseId, answer])
      );
      setAnswers(answersMap);
      console.log(`✓ Loaded ${exercisesData.answers.length} answers`);

      // Passo 3: Progresso
      setLoadingStep("progress");
      setProgress(exercisesData.progress);
      console.log(`✓ Loaded progress: ${exercisesData.progress.progressPercentage}%`);

      setLoadingStep("idle");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load module";
      setError(errorMessage);
      setLoadingStep("idle");
      console.error("Error loading module:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Avalia exercício e depois busca novo status/relatório - SERIAL
   * Ordem: 1. POST evaluate → 2. GET progress
   */
  const evaluateExercise = useCallback(
    async (
      exerciseId: string,
      answer: any,
      exerciseType: string,
      overrideModuleId?: string
    ): Promise<EvaluateExerciseResponse> => {
      const effectiveModuleId = overrideModuleId || moduleId;
      if (!effectiveModuleId) {
        throw new Error("Module not loaded");
      }

      setIsEvaluating(true);
      setError(null);

      try {
        // 1. Avaliar exercício
        console.log("→ Evaluating exercise...");
        const { evaluation, progress: newProgress } =
          await evaluateExerciseAndRefreshProgress({
            exerciseId,
            moduleId: effectiveModuleId,
            answer,
            exerciseType,
          });
        console.log("✓ Exercise evaluated");

        // Atualizar resposta localmente
        // Se a resposta da avaliação contém dados suficientes, usar; senão buscar answers
        const newAnswer: StudentExerciseAnswer = {
          exerciseId,
          studentAnswerData: JSON.stringify(answer),
          studentAnswerIsCorrect:
            evaluation.studentAnswerIsCorrect ?? evaluation.isCorrect,
          studentAnswerFeedback:
            evaluation.studentAnswerFeedback ?? evaluation.feedback ?? "",
          studentAnsweredAt:
            evaluation.studentAnsweredAt ?? new Date().toISOString(),
        };

        setAnswers((prev) => new Map(prev).set(exerciseId, newAnswer));

        // 2. Atualizar progresso com o que veio da API
        setProgress(newProgress);
        console.log(`✓ Progress updated: ${newProgress.progressPercentage}%`);

        if (evaluation.isModuleContentComplete === true) {
          navigate(`/student/modules/${effectiveModuleId}/completion`);
        }

        return evaluation;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to evaluate exercise";
        setError(message);
        throw err;
      } finally {
        setIsEvaluating(false);
      }
    },
    [moduleId, navigate]
  );

  // Helpers
  const getExercise = useCallback(
    (exerciseId: string) => exercises.find((e) => e.id === exerciseId),
    [exercises]
  );

  const getExerciseAnswer = useCallback(
    (exerciseId: string) => answers.get(exerciseId),
    [answers]
  );

  const isExerciseAnswered = useCallback(
    (exerciseId: string) => answers.has(exerciseId),
    [answers]
  );

  return (
    <ModuleExercisesContext.Provider
      value={{
        moduleId,
        exercises,
        answers,
        progress,
        isLoading,
        loadingStep,
        isEvaluating,
        error,
        loadModule,
        evaluateExercise,
        getExercise,
        getExerciseAnswer,
        isExerciseAnswered,
      }}
    >
      {children}
    </ModuleExercisesContext.Provider>
  );
};

/**
 * Hook opcional: undefined fora do ModuleExercisesProvider
 * (teste do módulo e nivelamento reutilizam o player sem o contexto do módulo).
 */
export const useModuleExercisesOptional = ():
  | ModuleExercisesContextType
  | undefined => useContext(ModuleExercisesContext);

/**
 * Hook para usar o Context de exercícios do módulo
 * @throws Error se usado fora do Provider
 */
export const useModuleExercises = (): ModuleExercisesContextType => {
  const context = useModuleExercisesOptional();
  if (!context) {
    throw new Error(
      "useModuleExercises must be used within ModuleExercisesProvider"
    );
  }
  return context;
};

